import React, { useState, useEffect } from 'react';
import { ownerAPI, fieldAPI } from '../lib/api';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Calendar, Clock, Plus, Trash2, Loader2, ChevronLeft, ChevronRight, Ban, CheckCircle } from 'lucide-react';

export default function OwnerCalendar() {
  const [loading, setLoading] = useState(true);
  const [fields, setFields] = useState([]);
  const [selectedField, setSelectedField] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [bookings, setBookings] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [blockData, setBlockData] = useState({
    start_time: '08:00',
    duration: 60,  // Duration in minutes
    reason: ''
  });

  useEffect(() => {
    loadFields();
  }, []);

  useEffect(() => {
    if (selectedField) {
      loadCalendarData();
    }
  }, [selectedField, selectedDate]);

  const loadFields = async () => {
    try {
      const response = await fieldAPI.getMy();
      setFields(response.data);
      if (response.data.length > 0) {
        setSelectedField(response.data[0].id);
      }
    } catch (error) {
      toast.error('Fehler beim Laden der Plätze');
    } finally {
      setLoading(false);
    }
  };

  const loadCalendarData = async () => {
    try {
      const [bookingsRes, blocksRes] = await Promise.all([
        ownerAPI.getBookings({ field_id: selectedField }),
        ownerAPI.getBlocks({ field_id: selectedField, date_from: selectedDate })
      ]);
      
      // Filter bookings for selected date
      const dateBookings = bookingsRes.data.filter(b => b.date === selectedDate);
      const dateBlocks = blocksRes.data.filter(b => b.date === selectedDate);
      
      setBookings(dateBookings);
      setBlocks(dateBlocks);
    } catch (error) {
      console.error('Error loading calendar data:', error);
    }
  };

  const handleCreateBlock = async () => {
    if (!selectedField || !blockData.start_time) {
      toast.error('Bitte alle Felder ausfüllen');
      return;
    }
    
    // Calculate end time based on duration
    const endTime = calculateEndTime(blockData.start_time, blockData.duration);
    
    // Validate end time doesn't exceed 23:00
    if (endTime > '23:00') {
      toast.error('Blockierung kann nicht über 23:00 hinausgehen');
      return;
    }
    
    try {
      await ownerAPI.createBlock({
        field_id: selectedField,
        date: selectedDate,
        start_time: blockData.start_time,
        end_time: endTime,
        reason: blockData.reason || 'Blockiert'
      });
      toast.success(`${blockData.duration} Min blockiert (${blockData.start_time} - ${endTime})`);
      setDialogOpen(false);
      setBlockData({ start_time: '08:00', duration: 60, reason: '' });
      loadCalendarData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Blockieren');
    }
  };

  const handleDeleteBlock = async (blockId) => {
    try {
      await ownerAPI.deleteBlock(blockId);
      toast.success('Blockierung entfernt');
      loadCalendarData();
    } catch (error) {
      toast.error('Fehler beim Entfernen');
    }
  };

  const changeDate = (days) => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() + days);
    setSelectedDate(date.toISOString().split('T')[0]);
  };

  // Generate time slots in 30-minute intervals (8:00 - 23:00)
  const timeSlots = [];
  for (let hour = 8; hour < 23; hour++) {
    timeSlots.push(`${hour.toString().padStart(2, '0')}:00`);
    timeSlots.push(`${hour.toString().padStart(2, '0')}:30`);
  }
  timeSlots.push('23:00'); // Add final slot

  // Duration options for blocking
  const durationOptions = [
    { label: '30 Min', minutes: 30 },
    { label: '60 Min', minutes: 60 },
    { label: '90 Min', minutes: 90 },
    { label: '2 Std', minutes: 120 },
    { label: '3 Std', minutes: 180 },
  ];

  const calculateEndTime = (startTime, durationMinutes) => {
    const [hours, mins] = startTime.split(':').map(Number);
    const totalMinutes = hours * 60 + mins + durationMinutes;
    const endHours = Math.floor(totalMinutes / 60);
    const endMins = totalMinutes % 60;
    return `${endHours.toString().padStart(2, '0')}:${endMins.toString().padStart(2, '0')}`;
  };

  const getSlotStatus = (time) => {
    // Calculate end of this 30-min slot
    const [hours, mins] = time.split(':').map(Number);
    const endMinutes = hours * 60 + mins + 30;
    const nextSlot = `${Math.floor(endMinutes / 60).toString().padStart(2, '0')}:${(endMinutes % 60).toString().padStart(2, '0')}`;
    
    // Check bookings
    const booking = bookings.find(b => 
      b.start_time < nextSlot && b.end_time > time
    );
    if (booking) {
      return { status: 'booked', data: booking };
    }
    
    // Check blocks
    const block = blocks.find(b => 
      b.start_time < nextSlot && b.end_time > time
    );
    if (block) {
      return { status: 'blocked', data: block };
    }
    
    return { status: 'free', data: null };
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-tunis-teal" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-tunis-blue">Kalender & Blockierungen</h1>
            <p className="text-muted-foreground">Verwalten Sie die Verfügbarkeit Ihrer Plätze</p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary" disabled={!selectedField}>
                <Plus className="w-4 h-4 mr-2" /> Zeit blockieren
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Zeitslot blockieren</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="font-medium">{fields.find(f => f.id === selectedField)?.name}</p>
                  <p className="text-sm text-muted-foreground">{selectedDate}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Startzeit</Label>
                    <Select value={blockData.start_time} onValueChange={(v) => setBlockData({...blockData, start_time: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {timeSlots.filter(t => t !== '23:00').map(time => (
                          <SelectItem key={time} value={time}>{time}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Dauer</Label>
                    <Select value={blockData.duration.toString()} onValueChange={(v) => setBlockData({...blockData, duration: parseInt(v)})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {durationOptions.map(opt => (
                          <SelectItem key={opt.minutes} value={opt.minutes.toString()}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {/* Preview of end time */}
                <div className="p-3 bg-tunis-blue/5 rounded-lg text-sm">
                  <span className="text-muted-foreground">Blockiert von </span>
                  <span className="font-semibold">{blockData.start_time}</span>
                  <span className="text-muted-foreground"> bis </span>
                  <span className="font-semibold">{calculateEndTime(blockData.start_time, blockData.duration)}</span>
                </div>
                <div>
                  <Label>Grund (optional)</Label>
                  <Input
                    value={blockData.reason}
                    onChange={(e) => setBlockData({...blockData, reason: e.target.value})}
                    placeholder="z.B. Wartung, Privat, Event"
                  />
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={() => setDialogOpen(false)}>
                    Abbrechen
                  </Button>
                  <Button className="flex-1 btn-primary" onClick={handleCreateBlock}>
                    Blockieren
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Controls */}
        <div className="flex flex-col md:flex-row gap-4">
          <Select value={selectedField} onValueChange={setSelectedField}>
            <SelectTrigger className="w-full md:w-[250px]">
              <SelectValue placeholder="Platz wählen" />
            </SelectTrigger>
            <SelectContent>
              {fields.map(field => (
                <SelectItem key={field.id} value={field.id}>{field.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={() => changeDate(-1)}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-[140px] sm:w-[180px]"
            />
            <Button variant="outline" size="icon" onClick={() => changeDate(1)}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-3 text-xs sm:text-sm">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 sm:w-4 sm:h-4 bg-green-100 border border-green-300 rounded"></div>
            <span>Frei</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 sm:w-4 sm:h-4 bg-blue-100 border border-blue-300 rounded"></div>
            <span>Gebucht</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 sm:w-4 sm:h-4 bg-red-100 border border-red-300 rounded"></div>
            <span>Blockiert</span>
          </div>
        </div>

        {/* Calendar Grid */}
        <Card>
          <CardHeader className="pb-2 sm:pb-4">
            <CardTitle className="flex items-center gap-2 text-sm sm:text-base">
              <Calendar className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="truncate">
                {new Date(selectedDate).toLocaleDateString('de-DE', { weekday: 'short', day: 'numeric', month: 'short' })}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {fields.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">Keine Plätze vorhanden</p>
            ) : (
              <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-10 gap-1.5 sm:gap-2">
                {timeSlots.map(time => {
                  const slotInfo = getSlotStatus(time);
                  const bgColor = slotInfo.status === 'booked' ? 'bg-blue-100 border-blue-300' 
                    : slotInfo.status === 'blocked' ? 'bg-red-100 border-red-300' 
                    : 'bg-green-100 border-green-300 hover:bg-green-200';
                  
                  return (
                    <div
                      key={time}
                      className={`p-3 rounded-lg border ${bgColor} transition-colors`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm">{time}</span>
                        {slotInfo.status === 'booked' && <CheckCircle className="w-4 h-4 text-blue-600" />}
                        {slotInfo.status === 'blocked' && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-red-600 hover:text-red-800"
                            onClick={() => handleDeleteBlock(slotInfo.data.id)}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                      {slotInfo.status === 'booked' && (
                        <p className="text-xs text-blue-700 mt-1 truncate">{slotInfo.data.user_name}</p>
                      )}
                      {slotInfo.status === 'blocked' && (
                        <p className="text-xs text-red-700 mt-1 truncate">{slotInfo.data.reason}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Blocks List */}
        {blocks.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Ban className="w-5 h-5 text-red-500" />
                Aktive Blockierungen für diesen Tag
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {blocks.map(block => (
                  <div key={block.id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200">
                    <div>
                      <p className="font-medium">{block.start_time} - {block.end_time}</p>
                      <p className="text-sm text-muted-foreground">{block.reason}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-800"
                      onClick={() => handleDeleteBlock(block.id)}
                    >
                      <Trash2 className="w-4 h-4 mr-1" /> Entfernen
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
